import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { safeLocalStorage } from '@/lib/safe-storage';
import { getMoneyRules, getAvailableStatusRollRule, getStatusRollRule } from '@/lib/money-rules';
import type { Product } from '@/types';
import type { ProductWithCategories } from '@/lib/api/products';

// ========== ДЕНЕЖНЫЕ ПОРОГИ И ПОДАРОЧНЫЕ ТОВАРЫ ==========
//
// Чисел здесь больше НЕТ. Пороги подарков, бесплатной доставки и id подарочных
// роллов приходят с сервера (GET /api/money-rules → @/lib/money-rules) и
// правятся в админке. Раньше они были переписаны сюда руками с комментарием
// «менять во всех трёх местах одновременно» — мест на деле было шесть, и
// расхождение клиента с сервером выливалось в «total с фронта != серверный».
//
// Экспорты ниже сохранены под прежними именами, но читают АКТУАЛЬНОЕ значение
// в момент обращения (геттеры), а не копию, снятую при загрузке модуля.
// Компонентам, которые ПОКАЗЫВАЮТ порог, нужен хук `useMoneyRules()` — иначе
// они не перерисуются, когда значения приедут с сервера.

/**
 * id подарочных роллов. Имена исторические: THRESHOLD_1500 — это Silver с
 * порогом 2500 ₽. Связь «за что выдаётся ↔ какой товар» задаётся РОЛЬЮ (тип
 * ролла), а не именем константы и не арифметикой по id.
 *
 * Товар берётся через `getStatusRollRule()` — он сам подставляет значение по
 * умолчанию, если сервер этот тир не прислал. Прежние `?? 76` / `?? 78` / `?? 79`
 * прямо здесь были ещё одной копией того же списка: при смене подарочного ролла
 * в админке они бы разошлись с сервером молча, а расхождение клиента с сервером
 * по подаркам — это ровно тот случай «total с фронта != серверный».
 */
export const BONUS_ROLLS = {
  get FIRST_ORDER(): number {
    return getMoneyRules().first_order.product_id;
  },
  get THRESHOLD_1500(): number {
    return getStatusRollRule('silver').product_id;
  },
  get THRESHOLD_2500(): number {
    return getStatusRollRule('gold').product_id;
  },
  get THRESHOLD_3500(): number {
    return getStatusRollRule('platinum').product_id;
  },
};

/** Пороги статусных роллов. Источник тот же, что у id выше. */
export const STATUS_THRESHOLDS = {
  get SILVER(): number {
    return getStatusRollRule('silver').min_sum;
  },
  get GOLD(): number {
    return getStatusRollRule('gold').min_sum;
  },
  get PLATINUM(): number {
    return getStatusRollRule('platinum').min_sum;
  },
};

/**
 * Акция «бесплатная доставка»: доставка бесплатна, если стоимость зоны не выше
 * MAX_ZONE_COST и сумма товаров не ниже MIN_SUM. Итог всё равно подтверждает
 * сервер (orders.js) — здесь только показ.
 */
export const FREE_DELIVERY_PROMO = {
  get MIN_SUM(): number {
    return getMoneyRules().free_delivery.min_sum;
  },
  get MAX_ZONE_COST(): number {
    return getMoneyRules().free_delivery.max_zone_cost;
  },
};

// Bonus types
export type BonusType =
  | 'first_order'  // Pink - За первый заказ
  | 'silver'       // Gray - Silver (2500₽)
  | 'gold'         // Gold - Gold (3500₽)
  | 'platinum'     // Purple - Platinum (4500₽)
  | 'promo'        // Orange - Промокод
  | 'roulette';    // Info - Рулетка

// Bonus style configuration
export interface BonusStyle {
  type: BonusType;
  label: string;
  color: string;
  bgColorLight: string;
  bgColorDark: string;
  borderColorLight: string;
  borderColorDark: string;
}

// Bonus styles mapping
export const BONUS_STYLES: Record<BonusType, BonusStyle> = {
  first_order: {
    type: 'first_order',
    label: 'За 1-й заказ',
    color: 'var(--pink-500)',
    bgColorLight: 'var(--pink-50)',
    bgColorDark: 'color-mix(in srgb, var(--pink-500) 15%, transparent)',
    borderColorLight: 'var(--pink-200)',
    borderColorDark: 'color-mix(in srgb, var(--pink-500) 30%, transparent)',
  },
  silver: {
    type: 'silver',
    label: 'Silver',
    color: 'var(--bonus-silver)',
    bgColorLight: 'var(--surface-secondary)',
    bgColorDark: 'color-mix(in srgb, var(--bonus-silver) 10%, transparent)',
    borderColorLight: 'var(--border-default)',
    borderColorDark: 'color-mix(in srgb, var(--bonus-silver) 20%, transparent)',
  },
  gold: {
    type: 'gold',
    label: 'Gold',
    color: 'var(--bonus-gold)',
    bgColorLight: 'var(--surface)',
    bgColorDark: 'color-mix(in srgb, var(--bonus-gold) 15%, transparent)',
    borderColorLight: 'color-mix(in srgb, var(--bonus-gold) 30%, var(--surface))',
    borderColorDark: 'color-mix(in srgb, var(--bonus-gold) 30%, transparent)',
  },
  platinum: {
    type: 'platinum',
    label: 'Platinum',
    color: 'var(--bonus-platinum)',
    bgColorLight: 'var(--violet-50)',
    bgColorDark: 'color-mix(in srgb, var(--bonus-platinum) 15%, transparent)',
    borderColorLight: 'var(--violet-200)',
    borderColorDark: 'color-mix(in srgb, var(--bonus-platinum) 30%, transparent)',
  },
  promo: {
    type: 'promo',
    label: 'Подарок',
    color: 'var(--warning)',
    bgColorLight: 'var(--warning-bg)',
    bgColorDark: 'color-mix(in srgb, var(--warning) 15%, transparent)',
    borderColorLight: 'color-mix(in srgb, var(--warning) 30%, var(--surface))',
    borderColorDark: 'color-mix(in srgb, var(--warning) 30%, transparent)',
  },
  roulette: {
    type: 'roulette',
    label: 'Рулетка',
    color: 'var(--info)',
    bgColorLight: 'var(--info-bg)',
    bgColorDark: 'color-mix(in srgb, var(--info) 15%, transparent)',
    borderColorLight: 'color-mix(in srgb, var(--info) 30%, var(--surface))',
    borderColorDark: 'color-mix(in srgb, var(--info) 30%, transparent)',
  },
};

// Get bonus style for a product ID
export const getBonusStyleForProduct = (productId: number): BonusStyle | null => {
  switch (productId) {
    case BONUS_ROLLS.FIRST_ORDER:
      return BONUS_STYLES.first_order;
    case BONUS_ROLLS.THRESHOLD_1500:
      return BONUS_STYLES.silver;
    case BONUS_ROLLS.THRESHOLD_2500:
      return BONUS_STYLES.gold;
    case BONUS_ROLLS.THRESHOLD_3500:
      return BONUS_STYLES.platinum;
    default:
      return null;
  }
};

// Determine which status roll is available based on promo eligible price.
// Ступени приходят с сервера отсортированными по убыванию порога, поэтому
// первое совпадение и есть самый ценный доступный ролл — та же логика, что в
// orders.js. Прежняя цепочка if-ов дублировала пороги ещё раз.
export const getAvailableStatusRoll = (promoEligiblePrice: number): { id: number; type: BonusType } | null => {
  const roll = getAvailableStatusRollRule(promoEligiblePrice);
  return roll ? { id: roll.product_id, type: roll.type } : null;
};

// Cart item interface
export interface CartItem {
  id: number;
  product: ProductWithCategories | Product;
  quantity: number;
  isBonus?: boolean;
  bonusType?: BonusType;
}

// Cart state interface
interface CartState {
  items: CartItem[];
  isOpen: boolean;
  
  // Hydration state for SSR
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
  
  // Computed
  totalItems: number;
  totalPrice: number;
  promoEligiblePrice: number; // Price without bonus/333 items
  
  // Actions
  addItem: (product: ProductWithCategories | Product, quantity?: number) => void;
  removeItem: (productId: number) => void;
  updateQuantity: (productId: number, quantity: number) => void;
  clearCart: () => void;
  setCartOpen: (isOpen: boolean) => void;
  toggleCart: () => void;
  
  // Bonus items (first order bonus)
  addBonusItem: (product: ProductWithCategories | Product, bonusType?: BonusType) => void;
  removeBonusItems: () => void;
  
  // Promo code items (separate from status rolls)
  addPromoItem: (product: ProductWithCategories | Product) => void;
  removePromoItem: () => void;
  hasPromoItem: () => boolean;
  
  // Status rolls (Silver/Gold/Platinum)
  addStatusRoll: (product: ProductWithCategories | Product, bonusType: BonusType) => void;
  removeStatusRolls: () => void;
  hasStatusRoll: () => boolean;
  
  // Roulette
  addRouletteItem: (product: ProductWithCategories | Product, spinPrice: number) => void;
  removeRouletteItem: () => void;
  hasRouletteItem: () => boolean;

  // Sync
  setItems: (items: CartItem[]) => void;
  getItemQuantity: (productId: number) => number;
}

// Categories excluded from minimum order calculation
// Category 7 = Соусы
// Category 15 = Роллы -30%
const EXCLUDED_CATEGORY_IDS: number[] = [7, 15];

// Helper to check if product belongs to excluded category
const isExcludedCategoryProduct = (product: ProductWithCategories | Product): boolean => {
  // Check ProductWithCategories (has categories array)
  if ('categories' in product && Array.isArray(product.categories)) {
    return product.categories.some(cat => EXCLUDED_CATEGORY_IDS.includes(cat.id));
  }
  // Check Product (has category_id)
  if ('category_id' in product && typeof product.category_id === 'number') {
    return EXCLUDED_CATEGORY_IDS.includes(product.category_id);
  }
  return false;
};

// "Сет дня" — отдельная акция, не суммируется с порогом для статусных роллов / ролла за регистрацию
const isSetOfTheDayProduct = (product: ProductWithCategories | Product): boolean => {
  return Boolean((product as { is_set_of_the_day?: boolean }).is_set_of_the_day);
};

// Calculate totals
// promoEligiblePrice используется для порога СТАТУСНЫХ роллов (Silver/Gold/Platinum) —
// сет дня СУММИРУЕТСЯ с ним (включается в порог).
// Для ролла за регистрацию используется отдельный селектор `selectFirstOrderEligiblePrice`,
// который дополнительно исключает сет дня.
const calculateTotals = (items: CartItem[]) => {
  let totalItems = 0;
  let totalPrice = 0;
  let promoEligiblePrice = 0;

  items.forEach((item) => {
    const price = item.product.price * item.quantity;
    totalItems += item.quantity;
    totalPrice += price;

    // bonusType covers: 'roulette', 'promo', 'silver', 'gold', 'platinum', 'first_order'
    if (!item.isBonus && !item.bonusType && !isExcludedCategoryProduct(item.product)) {
      promoEligiblePrice += price;
    }
  });

  return { totalItems, totalPrice, promoEligiblePrice };
};

export const useCartStore = create<CartState>()(
  persist(
    immer((set, get) => ({
      items: [],
      isOpen: false,
      totalItems: 0,
      totalPrice: 0,
      promoEligiblePrice: 0,
      
      // Hydration state
      _hasHydrated: false,
      setHasHydrated: (state) => {
        set({ _hasHydrated: state });
      },

      addItem: (product, quantity = 1) => {
        set((state) => {
          // !item.bonusType обязателен: приз рулетки лежит с isBonus:false, и без
          // этой проверки обычная покупка того же ролла сливалась бы со строкой
          // приза, накручивая его количество (в мобилке именно так и получались
          // заказы с призом ×2 и ×3 при одном спине).
          const existingIndex = state.items.findIndex(
            (item) => item.id === product.id && !item.isBonus && !item.bonusType
          );

          if (existingIndex !== -1) {
            state.items[existingIndex].quantity += quantity;
          } else {
            state.items.push({
              id: product.id,
              product,
              quantity,
              isBonus: false,
            });
          }

          const totals = calculateTotals(state.items);
          state.totalItems = totals.totalItems;
          state.totalPrice = totals.totalPrice;
          state.promoEligiblePrice = totals.promoEligiblePrice;
        });

        // ── Behavioral tracking: add_to_cart ──
        // Lazy import чтобы не вызывать SSR-проблем (тег 'use client' сверху)
        import('@/lib/source-tracker').then(({ captureEvent }) => {
          captureEvent('add_to_cart', {
            product_id: product.id,
            product_name: product.name,
            product_sku: product.sku || null,
            quantity,
            price: product.price,
          }, { value: product.price * quantity, currency: 'RUB' });
        }).catch(() => { /* ignore */ });
      },

      removeItem: (productId) => {
        set((state) => {
          // Строк с одним id может быть две: платная и подарочная (промокод,
          // приз рулетки, статусный ролл — они лежат с isBonus:false + bonusType).
          // Фильтр по одному id уносил обе, т.е. удаление платной позиции забирало
          // с собой подарок. Удаляем адресно, тем же предикатом, что в updateQuantity.
          const isEditable = (item: CartItem) => !item.isBonus && !item.bonusType;
          const hasEditable = state.items.some((item) => item.id === productId && isEditable(item));

          state.items = hasEditable
            ? state.items.filter((item) => !(item.id === productId && isEditable(item)))
            // Платной строки нет — значит убирают саму спецпозицию (крестик на подарке).
            : state.items.filter((item) => item.id !== productId);

          const totals = calculateTotals(state.items);
          state.totalItems = totals.totalItems;
          state.totalPrice = totals.totalPrice;
          state.promoEligiblePrice = totals.promoEligiblePrice;
        });

        // ── Behavioral tracking: remove_from_cart ──
        import('@/lib/source-tracker').then(({ captureEvent }) => {
          captureEvent('remove_from_cart', { product_id: productId });
        }).catch(() => { /* ignore */ });
      },

      updateQuantity: (productId, quantity) => {
        set((state) => {
          // Количество меняем только у обычных позиций. Приз рулетки лежит с
          // isBonus:false и bonusType:'roulette' — без этой проверки find() мог
          // попасть в него и накрутить количество (правило: один спин — один приз).
          // Подарки и промокодная позиция тоже фиксированы.
          const isEditable = (item: { isBonus?: boolean; bonusType?: BonusType }) =>
            !item.isBonus && !item.bonusType;

          if (quantity <= 0) {
            state.items = state.items.filter(
              (item) => !(item.id === productId && isEditable(item))
            );
          } else {
            const item = state.items.find(
              (item) => item.id === productId && isEditable(item)
            );
            if (item) {
              item.quantity = quantity;
            }
          }

          const totals = calculateTotals(state.items);
          state.totalItems = totals.totalItems;
          state.totalPrice = totals.totalPrice;
          state.promoEligiblePrice = totals.promoEligiblePrice;
        });
      },

      clearCart: () => {
        set((state) => {
          state.items = [];
          state.totalItems = 0;
          state.totalPrice = 0;
          state.promoEligiblePrice = 0;
        });
      },

      setCartOpen: (isOpen) => {
        set((state) => {
          state.isOpen = isOpen;
        });

        // ── Behavioral tracking: cart_opened ──
        if (isOpen) {
          const state = get();
          import('@/lib/source-tracker').then(({ captureEvent }) => {
            captureEvent('cart_opened', {
              items_count: state.totalItems,
              cart_value: state.totalPrice,
            }, { value: state.totalPrice, currency: 'RUB' });
          }).catch(() => { /* ignore */ });
        }
      },

      toggleCart: () => {
        set((state) => {
          state.isOpen = !state.isOpen;
        });
      },

      addBonusItem: (product, bonusType) => {
        set((state) => {
          // Status rolls types to preserve
          const statusTypes: BonusType[] = ['silver', 'gold', 'platinum'];
          
          // Remove existing bonus item of the same type, but preserve status rolls, promo and roulette items
          state.items = state.items.filter((item) => {
            // Always preserve status rolls
            if (item.bonusType && statusTypes.includes(item.bonusType)) return true;
            // Always preserve promo items
            if (item.bonusType === 'promo') return true;
            // Always preserve roulette items
            if (item.bonusType === 'roulette') return true;

            // Remove existing first order bonus when adding new one
            if (item.bonusType === 'first_order') return false;
            if (item.isBonus && !item.bonusType) return false;
            return true;
          });

          // Determine bonus type based on product ID if not provided
          const determinedBonusType = bonusType || getBonusStyleForProduct(product.id)?.type;

          state.items.push({
            id: product.id,
            product,
            quantity: 1,
            isBonus: true,
            bonusType: determinedBonusType,
          });

          const totals = calculateTotals(state.items);
          state.totalItems = totals.totalItems;
          state.totalPrice = totals.totalPrice;
          state.promoEligiblePrice = totals.promoEligiblePrice;
        });
      },

      removeBonusItems: () => {
        set((state) => {
          // Remove only first order bonus, NOT status rolls, promo or roulette items
          state.items = state.items.filter(
            (item) => {
              // Keep status rolls (silver, gold, platinum)
              const statusTypes: BonusType[] = ['silver', 'gold', 'platinum'];
              if (item.bonusType && statusTypes.includes(item.bonusType)) return true;
              // Keep promo items
              if (item.bonusType === 'promo') return true;
              // Keep roulette items
              if (item.bonusType === 'roulette') return true;
              // Remove other bonus items
              return !item.isBonus;
            }
          );

          const totals = calculateTotals(state.items);
          state.totalItems = totals.totalItems;
          state.totalPrice = totals.totalPrice;
          state.promoEligiblePrice = totals.promoEligiblePrice;
        });
      },

      // Promo code items management (separate from other bonuses)
      addPromoItem: (product) => {
        set((state) => {
          // Remove only existing promo item, NOT status rolls or first order bonus
          state.items = state.items.filter((item) => item.bonusType !== 'promo');

          state.items.push({
            id: product.id,
            product,
            quantity: 1,
            isBonus: false, // Not a regular bonus, it's a promo
            bonusType: 'promo',
          });

          const totals = calculateTotals(state.items);
          state.totalItems = totals.totalItems;
          state.totalPrice = totals.totalPrice;
          state.promoEligiblePrice = totals.promoEligiblePrice;
        });
      },

      removePromoItem: () => {
        set((state) => {
          state.items = state.items.filter((item) => item.bonusType !== 'promo');

          const totals = calculateTotals(state.items);
          state.totalItems = totals.totalItems;
          state.totalPrice = totals.totalPrice;
          state.promoEligiblePrice = totals.promoEligiblePrice;
        });
      },

      hasPromoItem: () => {
        return get().items.some((item) => item.bonusType === 'promo');
      },

      // Status rolls management (Silver/Gold/Platinum)
      addStatusRoll: (product, bonusType) => {
        set((state) => {
          // Remove any existing status rolls (silver, gold, platinum)
          const statusTypes: BonusType[] = ['silver', 'gold', 'platinum'];
          state.items = state.items.filter(
            (item) => !item.bonusType || !statusTypes.includes(item.bonusType)
          );

          state.items.push({
            id: product.id,
            product,
            quantity: 1,
            isBonus: true,
            bonusType,
          });

          const totals = calculateTotals(state.items);
          state.totalItems = totals.totalItems;
          state.totalPrice = totals.totalPrice;
          state.promoEligiblePrice = totals.promoEligiblePrice;
        });
      },

      removeStatusRolls: () => {
        set((state) => {
          const statusTypes: BonusType[] = ['silver', 'gold', 'platinum'];
          state.items = state.items.filter(
            (item) => !item.bonusType || !statusTypes.includes(item.bonusType)
          );

          const totals = calculateTotals(state.items);
          state.totalItems = totals.totalItems;
          state.totalPrice = totals.totalPrice;
          state.promoEligiblePrice = totals.promoEligiblePrice;
        });
      },

      hasStatusRoll: () => {
        const statusTypes: BonusType[] = ['silver', 'gold', 'platinum'];
        return get().items.some(
          (item) => item.bonusType && statusTypes.includes(item.bonusType)
        );
      },

      // Roulette item management
      addRouletteItem: (product, spinPrice) => {
        set((state) => {
          // Remove existing roulette item if any
          state.items = state.items.filter((item) => item.bonusType !== 'roulette');

          state.items.push({
            id: product.id,
            product: { ...product, price: spinPrice },
            quantity: 1,
            isBonus: false,
            bonusType: 'roulette',
          });

          const totals = calculateTotals(state.items);
          state.totalItems = totals.totalItems;
          state.totalPrice = totals.totalPrice;
          state.promoEligiblePrice = totals.promoEligiblePrice;
        });
      },

      removeRouletteItem: () => {
        set((state) => {
          state.items = state.items.filter((item) => item.bonusType !== 'roulette');

          const totals = calculateTotals(state.items);
          state.totalItems = totals.totalItems;
          state.totalPrice = totals.totalPrice;
          state.promoEligiblePrice = totals.promoEligiblePrice;
        });
      },

      hasRouletteItem: () => {
        return get().items.some((item) => item.bonusType === 'roulette');
      },

      setItems: (items) => {
        set((state) => {
          state.items = items;

          const totals = calculateTotals(items);
          state.totalItems = totals.totalItems;
          state.totalPrice = totals.totalPrice;
          state.promoEligiblePrice = totals.promoEligiblePrice;
        });
      },

      getItemQuantity: (productId) => {
        // Тот же предикат, что в addItem/updateQuantity: подарок и приз лежат с
        // isBonus:false + bonusType, и без проверки bonusType их количество
        // выдавалось за количество платной позиции.
        const item = get().items.find(
          (item) => item.id === productId && !item.isBonus && !item.bonusType
        );
        return item?.quantity || 0;
      },
    })),
    {
      name: 'aziatochka-cart',
      storage: createJSONStorage(() => safeLocalStorage),
      partialize: (state) => ({
        items: state.items,
      }),
      // Validate and clean up items loaded from localStorage
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Filter out invalid items (missing product or name)
          const validItems = state.items.filter(
            (item) => item?.product?.id && item?.product?.name && item?.product?.price !== undefined
          );
          // Always recalculate totals after hydration (they are not persisted)
          state.setItems(validItems);
          // Mark hydration as complete
          state.setHasHydrated(true);
        }
      },
    }
  )
);

// Selectors for performance
export const selectCartItems = (state: CartState) => state.items;
export const selectCartTotal = (state: CartState) => state.totalPrice;
export const selectCartItemsCount = (state: CartState) => state.totalItems;
export const selectIsCartOpen = (state: CartState) => state.isOpen;
export const selectPromoEligiblePrice = (state: CartState) => state.promoEligiblePrice;

// Бесплатные соусы/приборы (product id) — НЕ учитываются в МИНИМАЛЬНОЙ сумме заказа.
// Исключаем строго по id (а НЕ по категории «Соусы»), т.к. в этой категории есть и
// платные соусы (id 102-106), которые в минимум входят. Доставка сюда не включается.
// Соусы при этом остаются в чеке и оплачиваются — это только сумма для проверки минимума.
export const FREE_CONDIMENT_IDS: number[] = [1, 2, 3, 4, 5, 6, 7];
export const selectMinOrderEligibleTotal = (state: CartState): number => {
  let sum = 0;
  state.items.forEach((item) => {
    if (item.isBonus || item.bonusType) return;
    if (FREE_CONDIMENT_IDS.includes(item.product.id)) return;
    sum += item.product.price * item.quantity;
  });
  return sum;
};

// Сумма для порога ролла за регистрацию: тот же promoEligiblePrice минус сет дня.
// Логика синхронна с бэком (orders.js → firstOrderEligiblePrice).
export const selectFirstOrderEligiblePrice = (state: CartState): number => {
  let sum = 0;
  state.items.forEach((item) => {
    if (item.isBonus || item.bonusType) return;
    if (isExcludedCategoryProduct(item.product)) return;
    if (isSetOfTheDayProduct(item.product)) return;
    sum += item.product.price * item.quantity;
  });
  return sum;
};
export const selectHasHydrated = (state: CartState) => state._hasHydrated;

// ========== AUTO-SYNC STATUS ROLLS ==========
// Automatically remove/downgrade status rolls when promoEligiblePrice changes
// This runs at the store level so it works regardless of which UI is mounted

const STATUS_TYPES: BonusType[] = ['silver', 'gold', 'platinum'];

const getThresholdForType = (type: BonusType): number => {
  switch (type) {
    case 'silver': return STATUS_THRESHOLDS.SILVER;
    case 'gold': return STATUS_THRESHOLDS.GOLD;
    case 'platinum': return STATUS_THRESHOLDS.PLATINUM;
    default: return 0;
  }
};

useCartStore.subscribe((state, prevState) => {
  // Only react when promoEligiblePrice actually changed
  if (state.promoEligiblePrice === prevState.promoEligiblePrice) return;
  // Wait for hydration
  if (!state._hasHydrated) return;

  const currentRoll = state.items.find(
    (item) => item.bonusType && STATUS_TYPES.includes(item.bonusType)
  );

  if (!currentRoll?.bonusType) return;

  const threshold = getThresholdForType(currentRoll.bonusType);

  // Current roll is still valid
  if (state.promoEligiblePrice >= threshold) return;

  // Price dropped below threshold — check if we should downgrade or remove
  const target = getAvailableStatusRoll(state.promoEligiblePrice);

  if (!target) {
    // No roll eligible anymore — remove
    useCartStore.getState().removeStatusRolls();
  } else if (target.type !== currentRoll.bonusType) {
    // Need to downgrade, but we don't have product data here.
    // Just remove the current one — StatusRollsDisplay will re-add the correct one
    // when the big cart is opened next time.
    useCartStore.getState().removeStatusRolls();
  }
});

// ========== AUTO-SYNC РОЛЛА ЗА ПЕРВЫЙ ЗАКАЗ ==========
//
// Ролл за первый заказ выдаётся от порога first_order.min_sum. Раньше, если покупатель
// добавил товаров, получил подарок, а потом убрал часть корзины и сумма опустилась
// ниже порога, подарок ОСТАВАЛСЯ на экране. Сервер его всё равно не принимал —
// orders.js пересобирает бонусы сам, — и человек узнавал о пропаже уже из чека.
// Снимаем ролл сразу, как только условие перестало выполняться.
//
// Считается по selectFirstOrderEligiblePrice (без «Сета дня» и исключённых категорий),
// а НЕ по promoEligiblePrice — у статусных роллов своя сумма, и подписка выше
// на это изменение не реагирует.
useCartStore.subscribe((state, prevState) => {
  // Реагируем только на реальное изменение состава корзины
  if (state.items === prevState.items) return;
  if (!state._hasHydrated) return;

  const hasFirstOrderBonus = state.items.some((item) => item.bonusType === 'first_order');
  if (!hasFirstOrderBonus) return;

  // Порог читается на момент проверки: он приходит с сервера и может
  // отличаться от зашитого значения по умолчанию.
  if (selectFirstOrderEligiblePrice(state) < getMoneyRules().first_order.min_sum) {
    useCartStore.getState().removeBonusItems();
  }
});

